import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { getCached, setCached, invalidate } from '../lib/queryCache'
import { C, R, F, btnPrimary, btnSecondary, btnGhost, card, input, kpiCard, pageHeader, statusBadge, spinner } from '../lib/designTokens'

const Icon = ({ d, size=14 }) => <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d={d}/></svg>
const BuildingIcon = () => <Icon d="M2 2h12v12H2zM6 2v12M10 2v12M2 6h12M2 10h12" size={20}/>

const planColors = {
  free:       { bg:'#F3F4F6', color:'#6B7280', border:'#E5E7EB' },
  pro:        { bg:'#DBEAFE', color:'#1D4ED8', border:'#BFDBFE' },
  enterprise: { bg:'#EDE9FE', color:'#6D28D9', border:'#DDD6FE' },
}

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => (
  <div style={{position:'fixed',bottom:24,right:24,zIndex:999,display:'flex',alignItems:'center',gap:10,padding:'11px 18px',borderRadius:R.card,boxShadow:'0 4px 20px rgba(0,0,0,.18)',fontSize:F.body,fontWeight:600,background:type==='success'?C.green:C.red,color:'#fff',minWidth:240}}>
    <span style={{flex:1}}>{message}</span>
    <button onClick={onClose} style={{background:'none',border:'none',color:'rgba(255,255,255,.7)',cursor:'pointer',fontSize:16,lineHeight:1}}>×</button>
  </div>
)

// ─── Org Modal ────────────────────────────────────────────────────────────────
const OrgModal = ({ org, onSave, onClose }) => {
  const [form, setForm] = useState(org || { name:'', name_ar:'', plan:'free', logo_url:'', cr_number:'', vat_number:'', address:'', phone:'' })
  const [uploading, setUploading] = useState(false)
  const [uploadErr, setUploadErr] = useState('')
  const h = (k,v) => setForm(f=>({...f,[k]:v}))

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { setUploadErr('Please select an image file'); return }
    if (file.size > 2*1024*1024) { setUploadErr('Logo must be under 2MB'); return }
    setUploadErr('')
    setUploading(true)
    const ext = file.name.split('.').pop()
    const path = `org-logos/${Date.now()}.${ext}`
    const { error: upErr } = await supabase.storage.from('logos').upload(path, file, { upsert: true })
    if (upErr) {
      setUploading(false)
      setUploadErr('Upload failed: ' + upErr.message + (upErr.message?.includes('not found') ? ' — create a public "logos" bucket in Supabase Storage first.' : ''))
      return
    }
    const { data: pub } = supabase.storage.from('logos').getPublicUrl(path)
    h('logo_url', pub.publicUrl)
    setUploading(false)
  }

  return (
    <div style={{position:'fixed',inset:0,zIndex:200,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,.45)',backdropFilter:'blur(2px)'}} onClick={onClose}/>
      <div style={{position:'relative',background:'#fff',borderRadius:R.card+4,boxShadow:'0 20px 60px rgba(0,0,0,.2)',width:'100%',maxWidth:480,margin:'0 16px',padding:28,maxHeight:'90vh',overflowY:'auto'}}>
        <h3 style={{fontSize:F.heading-2,fontWeight:700,color:C.ink,marginBottom:20}}>{org?'Edit Organization':'New Organization'}</h3>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>

          {/* Logo upload */}
          <div>
            <label style={{display:'block',fontSize:F.caption,fontWeight:600,color:C.sub,marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Company Logo</label>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <div style={{width:56,height:56,borderRadius:R.input,border:`1px solid ${C.border}`,background:C.pageBg,display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden',flexShrink:0}}>
                {form.logo_url ? <img src={form.logo_url} alt="logo" style={{width:'100%',height:'100%',objectFit:'contain'}}/> : <span style={{fontSize:10,color:C.muted}}>No logo</span>}
              </div>
              <div style={{flex:1}}>
                <label style={{...btnSecondary,display:'inline-flex',cursor:'pointer',fontSize:F.caption}}>
                  {uploading ? 'Uploading…' : form.logo_url ? 'Replace Logo' : 'Upload Logo'}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={uploading} style={{display:'none'}}/>
                </label>
                {form.logo_url && (
                  <button onClick={()=>h('logo_url','')} style={{...btnGhost,fontSize:F.caption,color:C.red,marginLeft:8}}>Remove</button>
                )}
                <p style={{fontSize:10,color:C.muted,marginTop:4}}>PNG/JPG, saved once and reused on every printed PO/PR.</p>
                {uploadErr && <p style={{fontSize:10,color:C.red,marginTop:4}}>{uploadErr}</p>}
              </div>
            </div>
          </div>

          {[
            ['Organization Name (EN) *', 'name', 'text', 'e.g. Al-Rashid Construction'],
            ['اسم المنظمة (AR)', 'name_ar', 'text', 'مثال: شركة الرشيد للمقاولات'],
          ].map(([label,key,type,placeholder])=>(
            <div key={key}>
              <label style={{display:'block',fontSize:F.caption,fontWeight:600,color:C.sub,marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>{label}</label>
              <input type={type} value={form[key]||''} onChange={e=>h(key,e.target.value)} placeholder={placeholder}
                dir={key==='name_ar'?'rtl':'ltr'} style={{...input}}/>
            </div>
          ))}

          {/* CR / VAT — used on printed PO/PR header */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div>
              <label style={{display:'block',fontSize:F.caption,fontWeight:600,color:C.sub,marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>CR Number</label>
              <input value={form.cr_number||''} onChange={e=>h('cr_number',e.target.value)} placeholder="e.g. 4030219311" style={{...input}}/>
            </div>
            <div>
              <label style={{display:'block',fontSize:F.caption,fontWeight:600,color:C.sub,marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>VAT Number</label>
              <input value={form.vat_number||''} onChange={e=>h('vat_number',e.target.value)} placeholder="e.g. 310570953600003" style={{...input}}/>
            </div>
          </div>

          <div>
            <label style={{display:'block',fontSize:F.caption,fontWeight:600,color:C.sub,marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Address</label>
            <input value={form.address||''} onChange={e=>h('address',e.target.value)} placeholder="Company address" style={{...input}}/>
          </div>
          <div>
            <label style={{display:'block',fontSize:F.caption,fontWeight:600,color:C.sub,marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Phone</label>
            <input value={form.phone||''} onChange={e=>h('phone',e.target.value)} placeholder="+966 ..." style={{...input}}/>
          </div>

          <div>
            <label style={{display:'block',fontSize:F.caption,fontWeight:600,color:C.sub,marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Plan</label>
            <select value={form.plan} onChange={e=>h('plan',e.target.value)} style={{...input}}>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:24}}>
          <button onClick={onClose} style={{...btnSecondary,flex:1,justifyContent:'center'}}>Cancel</button>
          <button onClick={()=>onSave(form)} disabled={uploading} style={{...btnPrimary,flex:1,justifyContent:'center',opacity:uploading?.6:1}}>{org?'Save Changes':'Create'}</button>
        </div>
      </div>
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Organizations() {
  const { can } = useAuth()
  const isAdmin = !!can?.is_admin
  const [orgs,          setOrgs]          = useState([])
  const [loading,       setLoading]       = useState(true)
  const [modal,         setModal]         = useState(null)
  const [toast,         setToast]         = useState(null)
  const [projectCounts, setProjectCounts] = useState({})

  const showToast = (message, type='success') => { setToast({message,type}); setTimeout(()=>setToast(null),4000) }

  const loadOrgs = useCallback(async () => {
    setLoading(true)
    const [{data},{data:projects}] = await Promise.all([
      supabase.from('organizations').select('id,name,name_ar,plan,logo_url,cr_number,vat_number,address,phone,created_at').order('created_at',{ascending:false}),
      supabase.from('projects').select('organization_id'),
    ])
    setOrgs(data||[])
    const counts = {}
    ;(projects||[]).forEach(p=>{ counts[p.organization_id]=(counts[p.organization_id]||0)+1 })
    setProjectCounts(counts)
    setLoading(false)
  }, [])

  useEffect(()=>{ loadOrgs() },[loadOrgs])

  const handleSave = async (form) => {
    if (!form.name) return showToast('Organization name is required','error')
    const payload = { name:form.name, name_ar:form.name_ar||null, plan:form.plan, logo_url:form.logo_url||null,
      cr_number:form.cr_number||null, vat_number:form.vat_number||null, address:form.address||null, phone:form.phone||null }
    if (modal?.id) {
      const {error} = await supabase.from('organizations').update(payload).eq('id',modal.id)
      if (error) { showToast('Update failed: '+error.message,'error') } else { showToast('Organization updated'); setModal(null); loadOrgs() }
    } else {
      const {error} = await supabase.from('organizations').insert([payload])
      if (error) { showToast('Create failed: '+error.message,'error') } else { showToast('Created successfully'); setModal(null); loadOrgs() }
    }
  }

  const handleDelete = async (org) => {
    if (!window.confirm(`Delete "${org.name}"? This will affect all related projects.`)) return
    const {error} = await supabase.from('organizations').delete().eq('id',org.id)
    if (error) { showToast('Delete failed','error') } else { showToast('Deleted'); loadOrgs() }
  }

  return (
    <div style={{minHeight:'100vh',background:C.pageBg,fontFamily:'inherit'}}>

      {/* Header */}
      <div style={{...pageHeader,paddingLeft:24,paddingRight:24}}>
        <div>
          <div style={{fontSize:F.heading,fontWeight:800,color:C.ink,letterSpacing:'-.02em'}}>Organizations</div>
          <div style={{fontSize:F.caption,color:C.sub,marginTop:2}}>Manage your contracting companies</div>
        </div>
        <div style={{flex:1}}/>
        {isAdmin && (
          <button onClick={()=>setModal('add')} style={{...btnPrimary,gap:6}}>
            <svg width={14} height={14} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 2v12M2 8h12"/></svg>
            New Organization
          </button>
        )}
      </div>

      <div style={{padding:'20px 24px',maxWidth:1100,margin:'0 auto'}}>

        {/* KPI row */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
          {[
            { label:'Total Organizations', value:orgs.length,                                  color:C.ink },
            { label:'Total Projects',      value:Object.values(projectCounts).reduce((s,v)=>s+v,0), color:C.blue },
            { label:'Pro / Enterprise',    value:orgs.filter(o=>o.plan!=='free').length,       color:C.purple },
          ].map(k=>(
            <div key={k.label} style={{...kpiCard}}>
              <div style={{fontSize:F.label,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:C.muted,marginBottom:6}}>{k.label}</div>
              <div style={{fontSize:F.kpi,fontWeight:800,color:k.color,lineHeight:1.1}}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'60px 0',color:C.sub}}>
            <div style={{...spinner}}/>
            <span style={{fontSize:F.body}}>Loading...</span>
          </div>
        ) : orgs.length===0 ? (
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'64px 0',gap:12,color:C.muted}}>
            <div style={{width:56,height:56,borderRadius:16,background:C.pageBg,border:`2px dashed ${C.border2}`,display:'flex',alignItems:'center',justifyContent:'center'}}><BuildingIcon/></div>
            <div style={{fontSize:F.bodyLg,fontWeight:600,color:C.sub}}>No organizations yet</div>
            <div style={{fontSize:F.body}}>Create your first organization to get started</div>
            {isAdmin && <button onClick={()=>setModal('add')} style={{...btnPrimary,marginTop:4}}>+ Create Organization</button>}
          </div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:14}}>
            {orgs.map(org=>{
              const plan = planColors[org.plan]||planColors.free
              const count = projectCounts[org.id]||0
              return (
                <div key={org.id} style={{...card,transition:'box-shadow .15s'}}
                  onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,.08)'}
                  onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                  {/* Header */}
                  <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
                    {org.logo_url ? (
                      <img src={org.logo_url} alt={org.name} style={{width:40,height:40,borderRadius:R.input,objectFit:'cover',border:`1px solid ${C.border}`}}/>
                    ) : (
                      <div style={{width:40,height:40,borderRadius:R.input,background:C.blueBg,border:`1px solid ${C.blueLine}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,fontWeight:800,color:C.blue,flexShrink:0}}>
                        {org.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:F.title,fontWeight:700,color:C.ink,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{org.name}</div>
                      {org.name_ar && <div style={{fontSize:F.caption,color:C.sub,direction:'rtl'}}>{org.name_ar}</div>}
                    </div>
                    <span style={{fontSize:F.caption,fontWeight:600,padding:'2px 9px',borderRadius:R.badge,background:plan.bg,color:plan.color,border:`1px solid ${plan.border}`,flexShrink:0}}>
                      {org.plan}
                    </span>
                  </div>

                  {/* Footer */}
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',borderTop:`1px solid ${C.border}`,paddingTop:12}}>
                    <span style={{fontSize:F.caption,color:C.muted}}>{count} project{count!==1?'s':''}</span>
                    <div style={{display:'flex',gap:12}}>
                      {isAdmin && (
                        <button onClick={()=>setModal(org)} style={{...btnGhost,color:C.blue,fontSize:F.caption,fontWeight:600}}>Edit</button>
                      )}
                      {isAdmin && (
                        <button onClick={()=>handleDelete(org)} style={{...btnGhost,color:C.red,fontSize:F.caption,fontWeight:600}}>Delete</button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {(modal==='add'||(modal&&modal.id)) && <OrgModal org={modal==='add'?null:modal} onSave={handleSave} onClose={()=>setModal(null)}/>}
      {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
